
import { graft } from '../utilities/Graft';
import { Signal } from '../utilities/async/Signal';
import { IItemCacheThunkAction, IItemCacheDispatch } from '../store/actions/ItemCacheActions';
import { asOnceOnly } from '../store/middleware/RunOnceMiddleware';
import { Key, KeyFacet } from '../models/KeyFacet';
import {
  IOperationPayload,
  IOperationHandler,
  getOperationTypeKey,
  processOperation,
  getOperationState,
  editQueue,
  IOperationHandlerPayload,
  cleanupOperationQueue,
  IQueuedOperation,
  ItemOperationError
} from '../store/actions/implementOperation';
import { IFacet } from '../models/Facet';
import { registerOperators, getOperationKeyParts } from '../store/reducers/operationMapReducer';
import { waitForNextStoreUpdate } from '../store/enhancers/NextStoreUpdateEnhancer';
import { CustomError, ICustomErrorProps } from '@ms/utilities-error/lib/index';
import { handleError } from '../operations/handleError';
import { mapObject } from '../utilities/objects';
import { updateItems } from '../store/reducers/itemMapReducer';
import {
  isOperationHandlerRegisteredFacet,
  operationQueueHandlerFacet,
  operationHandlerConfigurationFacet
} from '../facets/OperationFacets';
import { TimeoutError } from '../utilities/async/TimeoutError';
import { readConfiguration } from '../operations/configuration/readConfiguration';
import { CancelError } from '../utilities/async/CancelError';
import ErrorStrings from '../models/error/ErrorStrings.resx';

/**
 * The default timeout duration allotted to the callbacks provided for operation handlers.
 * 1 minute is close to most browsers' default request timeouts, so this should not cause
 * much expected disruptions when working in web space. But it should help other runtime
 * environments prevent 'infinite' loading situations.
 */
const DEFAULT_OPERATION_TIMEOUT = 60000; // 1 minute

/**
 * @public
 */
export type IAddon = IItemCacheThunkAction<void>;

/**
 * Creates a new 'addon' with a one-time initializer.
 *
 * An addon may be dispatched to an item cache store to initialize its behaviors.
 * An addon will only execute its initialization the first time it is dispatched to a store.
 * @public
 */
export function createAddon(initializer: () => IItemCacheThunkAction<void>): IItemCacheThunkAction<void> {
  return asOnceOnly(initializer());
}

/**
 * @public
 */
export interface ISingleOperationPayload<TKey extends object, TOptions> {
  itemKey: Key<TKey>;
  options: TOptions;
}

/**
 * The payload provided to the callback for batch operation handlers.
 * @public
 */
export interface IBatchOperationPayload<TKey extends object, TOptions> extends IOperationPayload<TOptions> {
  keyType: TKey;
  items: {
    [itemKey: string]: TOptions;
  };
}

/**
 * Creates an action which, when dispatched, registers an operation handler,
 * which is typically an event-driven worker which processes a specified operation queue.
 * @public
 */
export function registerOperationHandler<TKey extends object, TOperationType extends object>(
  keyFacet: KeyFacet<TKey>,
  operationTypeFacet: IFacet<TOperationType>
): (handler: IOperationHandler<TKey, TOperationType>) => IItemCacheThunkAction<void> {
  const operationTypeKey = getOperationTypeKey({
    keyFacet: keyFacet,
    operationTypeFacet: operationTypeFacet
  });

  return (handler: IOperationHandler<TKey, TOperationType>) => {
    return (dispatch: IItemCacheDispatch) => {
      const operator = dispatch(handler({
        key: operationTypeKey
      }));

      dispatch(registerOperators({
        operators: {
          [operationTypeKey]: operator
        }
      }));

      dispatch(updateItems({
        items: {
          [operationTypeKey]: {
            ...operationQueueHandlerFacet.pack(operator),
            ...isOperationHandlerRegisteredFacet.pack(true)
          }
        }
      }));

      dispatch(processOperation({
        key: operationTypeKey
      }));
    };
  };
}

/**
 * @public
 */
export interface IOperationHandlerErrorProps extends ICustomErrorProps {
  // Nothing.
}

/**
 * An error to be thrown by a single item operation handler which indicates
 * that the handler callback failed, and has recongnized the error.
 * @public
 */
export class OperationHandlerError extends CustomError {
  constructor(props: IOperationHandlerErrorProps = {}) {
    super(props);
  }
}

/**
 * @public
 */
export interface IBatchedItemsOperationHandlerErrorProps extends ICustomErrorProps {
  items: {
    [itemKey: string]: Error;
  };
}

/**
 * An error to be thrown by a batched item operation handler which indicates
 * that some items in the batched have failed, and the rest have succeeded.
 * The individual errors per-item should be instances of `OperationHandlerError`
 * if possible, which indicates that the error was recognized.
 * @public
 */
export class BatchedItemsOperationHandlerError extends CustomError {
  public readonly items: {
    [itemKey: string]: Error;
  };

  constructor(props: IBatchedItemsOperationHandlerErrorProps) {
    super(props);
    this.items = props.items;
  }
}

/**
 * @public
 */
export type IOperationHandlerThunkAction = IItemCacheThunkAction<Promise<void>>;

/**
 * Represents the options for configuration a single operation handler.
 * @public
 */
export interface ICreateSingleItemOperationHandlerOptions {
  /**
   * Whether or not to merge all pending operations for the same key.
   * The default is `true`.
   */
  mergeForSameItemKey?: boolean;
  /**
   * The maximum number of items which may be processed in parallel.
   * Default is `1`.
   */
  maxParallelOperationCount?: number;
  /**
   * The default timeout allotted to the callback, in milliseconds.
   * Once the timeout elapses, the operation will be treated as canceled.
   */
  timeout?: number;
  /**
   * Whether or not to process the most recent queued operations first.
   *
   * Default is 'false'
   */
  prioritizeRecent?: boolean;
  /**
   * How many operations should remain in the queue after an allocation pass has been made.
   * If greater or equal to 0, will cancel operations (per the prioritizeRecent value)
   * until the queue meets this value.
   *
   * Default is 'undefined'.
   */
  maxQueueSize?: number;
}

/**
 * Represents a factory for single item operation handlers.
 * When invoked, creates a handler which will execute the given callback for each item
 * in the target operation queue.
 * @public
 */
export interface ISingleItemOperationHandlerFactory {
  <TKey extends object, TOptions extends object>(
    singleOperator: (payload: ISingleOperationPayload<TKey, TOptions>) => IOperationHandlerThunkAction
  ): IOperationHandler<TKey, TOptions>;
}

/**
 * Implements a data source operator by processing operations in the queue
 * one item at a time.
 * Data sources should use this function if it is not practical to process multiple
 * operations simultaneously.
 * @public
 */
export function createSingleItemOperationHandler(options: ICreateSingleItemOperationHandlerOptions = {}):
  ISingleItemOperationHandlerFactory {
  const {
    mergeForSameItemKey = true,
    maxParallelOperationCount = 1,
    prioritizeRecent = false,
    maxQueueSize = undefined
  } = options;

  return <TKey extends object, TOptions extends object>(
    singleOperator: (payload: ISingleOperationPayload<TKey, TOptions>) => IOperationHandlerThunkAction
  ): IOperationHandler<TKey, TOptions> => {
    return (payload: IOperationHandlerPayload<TKey, TOptions>): IItemCacheThunkAction<() => void> => {
      const {
        key
      } = payload;

      return (dispatch: IItemCacheDispatch) => {
        let workerCount = 0;

        let pendingAllocation = false;

        interface ITask {
          operation: IQueuedOperation<TKey, TOptions>;
          startSignals: Signal<void>[];
          completeSignals: Signal<void>[];
        }

        const {
          operationHandlerConfiguration: {
            defaultTimeout: defaultOperationHandlerTimeout = DEFAULT_OPERATION_TIMEOUT,
            enableTimeout = false
          } = {}
        } = dispatch(readConfiguration({
          operationHandlerConfiguration: operationHandlerConfigurationFacet
        }));

        const {
          timeout = enableTimeout ? defaultOperationHandlerTimeout : undefined
        } = options;

        async function allocate(): Promise<void> {
          await dispatch(waitForNextStoreUpdate());

          const pendingTasks: ITask[] = [];

          while (workerCount < maxParallelOperationCount) {
            const operationState = dispatch(getOperationState({
              key: key
            }));

            if (operationState && operationState.queue && operationState.queue.length > 0) {
              const {
                queue: [...queue]
              } = operationState;

              let operation = prioritizeRecent ? queue.pop() : queue.shift();
              if (operation) {
                const newQueue: typeof queue = [];
                const startSignals = [operation.startSignal];
                const completeSignals = [operation.completeSignal];

                for (const nextOperation of queue) {
                  if (mergeForSameItemKey && nextOperation.itemKey === operation.itemKey) {
                    operation = graft(operation, {
                      options: nextOperation.options
                    });

                    startSignals.push(nextOperation.startSignal);
                    completeSignals.push(nextOperation.completeSignal);
                  } else {
                    newQueue.push(nextOperation);
                  }
                }

                workerCount++;

                pendingTasks.push({
                  operation,
                  startSignals,
                  completeSignals
                });

                const opsToCancel: typeof queue = [];
                if (workerCount === maxParallelOperationCount && typeof maxQueueSize === 'number') {
                  while (newQueue.length > maxQueueSize) {
                    const operationToCancel = prioritizeRecent ? newQueue.shift() : newQueue.pop();
                    if (operationToCancel) {
                      opsToCancel.push(operationToCancel);
                    }
                  }
                }

                dispatch(editQueue({
                  key: key,
                  queue: newQueue
                }));

                if (opsToCancel.length > 0) {
                  const error = new CancelError();
                  for (const cancelledOperation of opsToCancel) {
                    cancelledOperation.startSignal.reject(error);
                    cancelledOperation.completeSignal.reject(error);
                  }
                }

                // Since a task was allocated, attempt to allocate more tasks.

                continue;
              }
            }

            // Stop allocating tasks.

            break;
          }

          pendingAllocation = false;

          for (const task of pendingTasks) {
            work(task);
          }
        }

        async function work({
          operation,
          startSignals,
          completeSignals
        }: ITask): Promise<void> {
          for (const startSignal of startSignals) {
            // Indicate that work is starting since the operation will be dispatched to the handler.
            startSignal.resolve();
          }

          const {
            resolve,
            reject,
            promise
          } = new Signal<void>();

          try {
            await waitWithTimeout(dispatch(singleOperator({
              itemKey: operation.itemKey,
              options: <TOptions>operation.options
            })), timeout);

            resolve();
          } catch (e) {
            if (!(e instanceof OperationHandlerError)) {
              dispatch(handleError({
                error: e
              }));
            }

            reject(e instanceof Error ? e : new OperationHandlerError());
          }

          const {
            completeSignals: remainingCompleteSignals
          } = dispatch(cleanupOperationQueue({
            key: key,
            items: {
              [operation.itemKey]: [operation.options]
            }
          }));

          for (const signal of [...completeSignals, ...remainingCompleteSignals]) {
            promise.then(signal.resolve, signal.reject);
          }

          workerCount--;

          // Once a worker completes, it should refill the queue if possible.

          if (!pendingAllocation) {
            pendingAllocation = true;
            allocate();
          }
        }

        return () => {
          // Refill the queue if possible.

          if (!pendingAllocation) {
            pendingAllocation = true;
            allocate();
          }
        };
      };
    };
  };
}

/**
 * Represents the options for configuring a batched operation handler.
 * @public
 */
export interface ICreateBatchedItemOperationHandlerOptions<TKey extends object = {}, TOptions extends object = {}> {
  /**
   * The maximum number of batches which may be processed in parallel.
   * Default is `1`.
   */
  maxParallelOperationCount?: number;
  /**
   * The maximum number of distinct items to pull into a batch.
   * Default is all items.
   */
  maxItemCountPerBatch?: number;
  /**
   * Optional discriminator to determine if two operations should be treated as part of the same batch.
   * Default always returns `true` for any pair of operations.
   */
  areSameBatch?: (
    payload1: ISingleOperationPayload<TKey, TOptions>,
    payload2: ISingleOperationPayload<TKey, TOptions>
  ) => boolean;
  /**
   * The default timeout allotted to the callback, in milliseconds.
   * Once the timeout elapses, the operation will be treated as canceled.
   */
  timeout?: number;
}

/**
 * Represents a factory for batched item operation handlers.
 * When invoked, creates a handler which invokes the specified callback
 * across multiple items within the target operation queue.
 * @public
 */
export interface IBatchedItemsOperationHandlerFactory {
  <TKey extends object, TOptions extends object>(
    batchedOperator: (payload: IBatchOperationPayload<TKey, TOptions>
    ) => IOperationHandlerThunkAction): IOperationHandler<TKey, TOptions>;
}

/**
 * Implements a data source operator by processing all pending operations as a batch.
 * Data sources should use this function to minimize I/O operations when a lot
 * of items need to be processed.
 * @public
 */
export function createBatchedItemsOperationHandler(options: ICreateBatchedItemOperationHandlerOptions = {}):
  IBatchedItemsOperationHandlerFactory {
  const {
    maxParallelOperationCount = 1,
    maxItemCountPerBatch = Infinity,
    areSameBatch = defaultAreSameBatch
  } = options;

  return <TKey extends object, TOptions extends object>(
    batchedOperator: (payload: IBatchOperationPayload<TKey, TOptions>) => IOperationHandlerThunkAction
  ): IOperationHandler<TKey, TOptions> => {
    return (payload: IOperationHandlerPayload<TKey, TOptions>): IItemCacheThunkAction<() => void> => {
      const {
        key
      } = payload;

      return (dispatch: IItemCacheDispatch) => {
        let workerCount = 0;

        let pendingAllocation = false;

        interface ITask {
          batchOperation: IBatchOperationPayload<TKey, TOptions>;
          startSignals: Signal<void>[];
          completeSignals: Signal<void>[];
          completeSignalsMap: {
            [itemKey: string]: Signal<void>;
          };
        }

        const {
          operationHandlerConfiguration: {
            defaultTimeout: defaultOperationHandlerTimeout = DEFAULT_OPERATION_TIMEOUT,
            enableTimeout = false
          } = {}
        } = dispatch(readConfiguration({
          operationHandlerConfiguration: operationHandlerConfigurationFacet
        }));

        const {
          timeout = enableTimeout ? defaultOperationHandlerTimeout : undefined
        } = options;

        async function allocate(): Promise<void> {
          await dispatch(waitForNextStoreUpdate());

          const pendingTasks: ITask[] = [];

          while (workerCount < maxParallelOperationCount) {
            const operationState = dispatch(getOperationState({
              key: key
            }));

            if (operationState) {
              const {
                queue
              } = operationState;

              if (queue && queue.length > 0) {
                let batchOperation: IBatchOperationPayload<TKey, TOptions> = {
                  items: {}
                } as IBatchOperationPayload<TKey, TOptions>;

                const startSignals: Signal<void>[] = [];
                const completeSignals: Signal<void>[] = [];
                const completeSignalsMap: {
                  [itemKey: string]: Signal<void>;
                } = {};

                const newQueue: typeof queue = [];

                const [firstOperation] = queue;

                for (const operation of queue) {
                  if ((Object.keys(batchOperation.items).length >= maxItemCountPerBatch &&
                    !(operation.itemKey in batchOperation.items)) || (
                      firstOperation !== operation &&
                      !areSameBatch({
                        itemKey: firstOperation.itemKey,
                        options: firstOperation.options
                      }, {
                          itemKey: operation.itemKey,
                          options: operation.options
                        })
                    )) {
                    // Once the maximum number of items has been reached,
                    // or the operation is not deemed part of the same batch.
                    // skip this operation, leaving it in the queue.
                    newQueue.push(operation);
                  } else {
                    batchOperation = graft(batchOperation, {
                      items: {
                        [operation.itemKey]: operation.options
                      }
                    });

                    startSignals.push(operation.startSignal);
                    completeSignals.push(operation.completeSignal);
                    completeSignalsMap[operation.itemKey] = operation.completeSignal;
                  }
                }

                workerCount++;

                pendingTasks.push({
                  batchOperation,
                  startSignals,
                  completeSignals,
                  completeSignalsMap
                });

                dispatch(editQueue({
                  key: key,
                  queue: newQueue
                }));

                continue;
              }
            }

            // Stop allocating tasks.

            break;
          }

          pendingAllocation = false;

          for (const task of pendingTasks) {
            work(task);
          }
        }

        async function work({
          batchOperation,
          startSignals,
          completeSignals,
          completeSignalsMap
        }: ITask): Promise<void> {
          for (const startSignal of startSignals) {
            startSignal.resolve();
          }

          const {
            resolve,
            reject,
            promise
          } = new Signal<void>();

          try {
            await waitWithTimeout(dispatch(batchedOperator(batchOperation)), timeout);

            resolve();
          } catch (e) {
            if (!(e instanceof BatchedItemsOperationHandlerError)) {
              dispatch(handleError({
                error: e
              }));
            }

            if (e instanceof BatchedItemsOperationHandlerError) {
              for (const itemKey of Object.keys(completeSignalsMap)) {
                const operationItem = completeSignalsMap[itemKey];
                const itemError = e.items[itemKey];
                if (itemError) {
                  operationItem.reject(itemError instanceof Error ? itemError : new OperationHandlerError());
                } else {
                  operationItem.resolve();
                }
              }
            }

            reject(e instanceof Error ? e : new OperationHandlerError());
          }

          const {
            completeSignals: remainingCompleteSignals
          } = dispatch(cleanupOperationQueue({
            key: key,
            items: mapObject(batchOperation.items, (itemOptions: TOptions) => ([itemOptions]))
          }));

          for (const signal of [...completeSignals, ...remainingCompleteSignals]) {
            promise.then(signal.resolve, signal.reject);
          }

          workerCount--;

          if (!pendingAllocation) {
            pendingAllocation = true;
            allocate();
          }
        }

        return () => {
          if (!pendingAllocation) {
            pendingAllocation = true;
            allocate();
          }
        };
      };
    };
  };
}

/**
 * Data source operator which loads its implementation asynchronously on
 * the first execution.
 * @public
 */
export function createDeferredOperationHandler<TKey extends object, TOptions extends object>(
  loadOperator: () => Promise<IOperationHandler<TKey, TOptions>>
): IOperationHandler<TKey, TOptions> {
  return (payload: IOperationHandlerPayload<TKey, TOptions>) => {
    const {
      key
    } = payload;

    const {
      keyFacet,
      operationTypeFacet
    } = getOperationKeyParts(key);

    return (dispatch: IItemCacheDispatch) => {
      let loading = false;

      const load = async () => {
        try {
          const dataSourceOperator = await loadOperator();

          if (!dataSourceOperator) {
            throw new Error(ErrorStrings.DefaultTitle);
          }

          // Register the real operation handler, replacing the placeholder loader.
          dispatch(registerOperationHandler(keyFacet, operationTypeFacet)(dataSourceOperator));
        } catch (e) {
          const error = e instanceof Error ? e : new Error(ErrorStrings.DefaultTitle);

          const operationState = dispatch(getOperationState({
            key: key
          }));

          if (operationState && operationState.queue && operationState.queue.length > 0) {
            // If there are pending operations, and the handler has failed to load,
            // these operations should fail, which will indicate to the original caller that
            // it should consider retrying.

            const startSignals: Signal<void>[] = [];
            const completeSignals: Signal<void>[] = [];

            const itemErrors: { [itemKey: string]: Error } = {};

            for (const operation of operationState.queue) {
              startSignals.push(operation.startSignal);
              completeSignals.push(operation.completeSignal);

              itemErrors[operation.itemKey] = new ItemOperationError({
                innerError: error
              });
            }

            dispatch(editQueue({
              key,
              queue: []
            }));

            const itemOperationError = new BatchedItemsOperationHandlerError({
              innerError: error,
              items: itemErrors
            });

            for (const startSignal of startSignals) {
              startSignal.reject(itemOperationError);
            }

            for (const completeSignal of completeSignals) {
              completeSignal.reject(itemOperationError);
            }
          }

          dispatch(handleError({
            error
          }));
        } finally {
          // Reset loading state. If the operator failed to load,
          // triggering this operator will attempt to load it again.
          loading = false;
        }
      };

      return () => {
        // Placeholder operation handler which invokes the loader callback
        // as long as there are pending operations to process.
        const operationState = dispatch(getOperationState({
          key: key
        }));

        if (operationState && operationState.queue && operationState.queue.length > 0) {
          if (!loading) {
            loading = true;

            load();
          }
        }
      };
    };
  };
}

function defaultAreSameBatch(): boolean {
  return true;
}

async function waitWithTimeout(task: Promise<void>, timeout: number | undefined): Promise<void> {
  const {
    resolve,
    reject,
    promise
  } = new Signal<void>();

  task.then(() => resolve(), (error: Error) => reject(error));

  let timeoutId: number | undefined;

  if (typeof timeout === 'number') {
    timeoutId = setTimeout(() => {
      reject(new TimeoutError({ timeout }));
    }, timeout);
  }

  try {
    await promise;

  } finally {
    if (typeof timeoutId === 'number') {
      clearTimeout(timeoutId);
    }
  }
}
